import React from "react";
import PropTypes from "prop-types";
import { Icon, Popover } from "antd";
import EditableAnalysisField from "./editable-analysis-field";
import FraudIndicatorsCell from "./fraud-indicators-cell";
import {
    getBankStatementAnalysisList,
    getFraudSummary,
    getNormalizedAnalysisMap,
    isEditableAnalysisField,
} from "utilities/bank-statement-analyser-utils";

const renderAnalysisTitle = (label, formula, hideFormula) => (
    <span>
        {label}
        {formula && !hideFormula ? (
            <Popover content={formula} style={{ padding: "5px" }}>
                <Icon style={{ marginLeft: "5px" }} type="eye" />
            </Popover>
        ) : null}
    </span>
);

class BankStatementAnalysisSummary extends React.PureComponent {
    // One grid cell for one row of one column. Genuinely empty when a
    // column has fewer rows than the tallest column — still occupies the
    // grid slot so every OTHER column's rows keep lining up underneath it.
    renderCell(columnId, row, rowIndex, analysisMap, fraud) {
        if (!row) {
            return (
                <div
                    className="bsa-summary__cell bsa-summary__cell--empty"
                    key={`${columnId}__empty-${rowIndex}`}
                />
            );
        }
        const {
            normalizedStatementLoading,
            adminGroup,
            userGroup,
            recreditViewOnlyPermission,
            onEditField,
            hideFormula,
            compact,
            lakhsOnly,
        } = this.props;
        const body = row.fraud ? (
            <FraudIndicatorsCell
                stat
                small={row.small}
                count={fraud.count}
                indicators={fraud.indicators}
                loading={normalizedStatementLoading}
            />
        ) : (
            <EditableAnalysisField
                fieldKey={row.key}
                rawValue={analysisMap[row.key]}
                isLoading={normalizedStatementLoading}
                editable={isEditableAnalysisField(
                    row.key,
                    adminGroup,
                    recreditViewOnlyPermission,
                    userGroup
                )}
                compact={compact}
                lakhsOnly={lakhsOnly}
                small={row.small}
                onSave={(val) => onEditField(row.key, val)}
            />
        );
        const className =
            "bsa-summary__cell"
            + (rowIndex > 0 ? " bsa-summary__cell--divider" : "")
            + (row.small ? " bsa-summary__cell--sub" : "");
        return (
            <div className={className} key={`${columnId}__${row.key}`}>
                <div className="bsa-summary__label">
                    {renderAnalysisTitle(row.label, row.formula, hideFormula)}
                </div>
                {body}
            </div>
        );
    }

    render() {
        const { normalizedStatementStats, bankCode, windowMode } = this.props;
        const analysisMap = getNormalizedAnalysisMap(normalizedStatementStats, windowMode);
        const columns = getBankStatementAnalysisList(bankCode);
        const fraud = getFraudSummary(normalizedStatementStats);
        const rowCount = Math.max(...columns.map((c) => c.rows.length));

        // Emitted ROW-MAJOR (every column's row 0, then every column's row 1,
        // …) rather than column-major, so each row index is a single real CSS
        // grid row shared by every column — that's what keeps "ABB (6m)" and
        // "AV. monthly debit 6m" etc. vertically aligned regardless of label
        // wrapping, instead of each column stacking independently.
        const cells = [];
        for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
            columns.forEach((column) => {
                cells.push(
                    this.renderCell(column.id, column.rows[rowIndex], rowIndex, analysisMap, fraud)
                );
            });
        }

        return (
            <div
                className="bsa-summary bsa-summary--grid p-10"
                style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(110px, 1fr))` }}
            >
                {cells}
            </div>
        );
    }
}

BankStatementAnalysisSummary.propTypes = {
    normalizedStatementStats: PropTypes.object,
    normalizedStatementLoading: PropTypes.bool,
    bankCode: PropTypes.string,
    adminGroup: PropTypes.object,
    userGroup: PropTypes.array,
    recreditViewOnlyPermission: PropTypes.bool,
    onEditField: PropTypes.func.isRequired,
    hideFormula: PropTypes.bool,
    compact: PropTypes.bool,
    lakhsOnly: PropTypes.bool,
    // "complete" (default) reads the all-transactions windows; "credit"
    // reads the parallel credit-only windows for the same four fields
    // (AV./Total monthly debit/credit) — see getNormalizedAnalysisMap.
    windowMode: PropTypes.oneOf(["complete", "credit"]),
};

BankStatementAnalysisSummary.defaultProps = {
    normalizedStatementLoading: false,
    compact: false,
    lakhsOnly: false,
    windowMode: "complete",
};

export default BankStatementAnalysisSummary;
